import { useState } from 'react';
import { FileDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ErrorService } from '@/lib/errors/ErrorService';
import { triggerBlobDownload } from '@/utils/blobDownload';
import type { DisplayExchange } from '../../types/displayExchange';
import {
  generateDocxFromConversation,
  sanitizeFileName,
} from '../../utils/docxExport';

interface ConversationExportButtonProps {
  title?: string;
  exchanges: readonly DisplayExchange[];
  disabled?: boolean;
}

/**
 * Downloads the visible Case Discovery conversation as a clean Word document.
 *
 * The button owns only the browser-side side effects. The export module receives
 * the already unified transcript and deliberately projects out technical
 * timeline items before generating the DOCX.
 */
export function ConversationExportButton({
  title,
  exchanges,
  disabled = false,
}: ConversationExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const hasConversation = exchanges.length > 0;

  const handleExport = async () => {
    if (isExporting || disabled || !hasConversation) return;

    const exportTitle = title?.trim() || 'Recherche Hector';

    try {
      setIsExporting(true);
      const blob = await generateDocxFromConversation(exportTitle, exchanges);
      triggerBlobDownload(blob, `${sanitizeFileName(exportTitle)}.docx`);
      toast.success('Discussion téléchargée en Word');
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'export_conversation_word' } });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleExport}
            disabled={disabled || isExporting || !hasConversation}
            aria-label="Télécharger la discussion en Word"
            title="Télécharger la discussion en Word"
            className="h-8 w-8 shrink-0 p-0 text-grey-500 hover:bg-grey-100 hover:text-charcoal"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <FileDown className="h-4 w-4" aria-hidden />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          Télécharger la discussion en Word
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
